"use client";
import dayjs from "dayjs";
import { useContext, useState } from "react";

import { Button } from "@/components/base/button";
import { Heading } from "@/components/base/heading";
import { Link } from "@/components/base/link";
import { BookedButtonRaised } from "@/components/booking/booking-button/booked-button-raised";
import { BookingDialog } from "@/components/booking/booking-button/booking-dialog";
import { BookingNextActivitiesForFilters } from "@/components/booking/booking-next-activities/booking-next-activities-for-filters";
import { CalendarDaysIcon, ClockIcon, InfoCircleIcon, MapPinIconOutline, UsersIcon } from "@/components/icons/icons";
import { ContentContainer } from "@/components/layout/content-container";
import { FullWidthContainer } from "@/components/layout/full-width-container";
import { HorizontalContainer } from "@/components/layout/horizontal-container";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { VerticalContainer } from "@/components/layout/vertical-container";
import { VerticalSpacer } from "@/components/layout/vertical-spacer";
import { VisuallyHidden } from "@/components/layout/visually-hidden";
import { Breadcrumbs } from "@/components/navigation/breadcrumbs";
import { DynamicZone } from "@/components/ui/dynamic-zone";
import { ErrorMessage, ErrorMessageFlavour } from "@/components/ui/error-message";
import { HandleTextToRichText } from "@/components/ui/handle-text-to-richtext";
import { IconAndText } from "@/components/ui/icon-and-text";
import {
  MediaCard,
  type MediaCardHorizontalAlignmentType,
  type MediaCardVerticalAlignmentType,
} from "@/components/ui/media-card";
import { ShareButtons } from "@/components/ui/share-buttons";
import { Text } from "@/components/ui/text";
import type { TextTheme } from "@/components/ui/ui.model";
import { UserContext } from "@/contexts/user-context";
import { type ActivityFragment, FilterTypes, useBookableActivitySuspenseQuery } from "@/generated/client.generated";
import { useBooking } from "@/hooks/use-booking";
import { isBookable } from "@/utils/booking-utils";
import { paths } from "@/utils/paths-utils";
import { capitalizeWord } from "@/utils/string-utils";

require("dayjs/locale/nb");
dayjs.locale("nb");

interface GroupActivitySpecificProps {
  parts: ActivityFragment;
  bookingId: string;
  centerId: string;
}

export const GroupActivity = ({ parts, bookingId, centerId }: GroupActivitySpecificProps) => {
  const { myBookedActivities } = useContext(UserContext);

  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);

  const { data: bookableActivityData } = useBookableActivitySuspenseQuery({
    variables: {
      centerId: centerId,
      id: bookingId,
    },
  });

  const bookableActivity = bookableActivityData?.BookableGroupActivity;

  const { handleBooking, handleCancelBooking, loading, booking, bookingError, cancelError } = useBooking(
    bookableActivity?.id ?? "",
    myBookedActivities?.find((mba) => mba.BookableGroupActivity.id === bookableActivity?.id),
  );

  if (!bookableActivity) {
    return <></>;
  }

  const canBeBooked = isBookable(bookableActivity);
  const canBeCancelled = bookableActivity.cancel.isEnabled;

  const groupActivity = bookableActivity?.GroupActivity;

  const calendarEvent = {
    description: groupActivity?.page.metaDescription ?? "",
    end: bookableActivity.end,
    location: bookableActivity.center?.page.title ?? "",
    start: bookableActivity.start,
    summary: bookableActivity.name,
  };

  return (
    <>
      <VisuallyHidden>
        <Heading type="h1">{parts.page.title}</Heading>
      </VisuallyHidden>

      {parts.page?.responsiveMedia && (
        <>
          <FullWidthContainer spanTopMargin={true}>
            <MediaCard
              alignContentHorizontally={parts.page.alignContentHorizontally as MediaCardHorizontalAlignmentType}
              alignContentVertically={parts.page.alignContentVertically as MediaCardVerticalAlignmentType}
              buttonText={parts.page.headerButtonText ?? undefined}
              fallbackAlt={parts.page.title}
              heading={parts.page.headerHeading ?? undefined}
              media={parts.page.responsiveMedia}
              priority={true}
              text={parts.page.headerText ?? undefined}
              textTheme={parts.page.textTheme as TextTheme}
              url={parts.page.headerButtonUrl ?? undefined}
            />
          </FullWidthContainer>
          <HorizontalSpacer size="x-small" />
        </>
      )}

      <ContentContainer horizontalPlacement="left" width="medium">
        {booking ? (
          <>
            <BookedButtonRaised>
              <HorizontalContainer horizontalPlacement="space" verticalPlacement="center" width="full">
                <VerticalContainer>
                  <Text value="Du er påmeldt!" weight="bold" />
                  <Text value="Velkommen på trening" />
                </VerticalContainer>

                <Button
                  buttonType="neutral"
                  disabled={!canBeCancelled}
                  onClick={() => setDialogIsOpen(true)}
                  loading={loading}
                >
                  {booking?.__typename === "WaitingListBooking" ? "Avbook venteliste" : "Avbook time"}
                </Button>
              </HorizontalContainer>
            </BookedButtonRaised>
            <HorizontalSpacer size="medium" />

            {!canBeCancelled && (
              <>
                <HorizontalSpacer size="x-small" />
                <ErrorMessage
                  error={bookableActivity.cancel.code}
                  errorMessageFlavour={ErrorMessageFlavour.GROUP_ACTIVITY}
                />
              </>
            )}

            {cancelError && (
              <>
                <HorizontalSpacer size="x-small" />
                <ErrorMessage error={cancelError} errorMessageFlavour={ErrorMessageFlavour.GROUP_ACTIVITY} />
              </>
            )}

            <BookingDialog
              buttonText="Ja, avbook time"
              heading="Er du sikker på at du vil avbooke timen?"
              id={bookableActivity.name}
              isOpen={dialogIsOpen}
              onClickButton={handleCancelBooking}
              onCloseDialog={() => setDialogIsOpen(false)}
            />

            <HorizontalSpacer />

            <Breadcrumbs breadcrumbs={parts.page.breadcrumbs} hideBreadcrumbs={parts.page.hideBreadcrumbs} />
          </>
        ) : (
          <>
            <Breadcrumbs breadcrumbs={parts.page.breadcrumbs} hideBreadcrumbs={parts.page.hideBreadcrumbs} />

            <Button
              buttonType={bookableActivity.slot.available === 0 ? "tertiary" : "secondary"} // yellow button for waiting-list, blue for non-booked
              disabled={!canBeBooked.isEnabled}
              onClick={handleBooking}
              loading={loading}
            >
              {bookableActivity.slot.available === 0 ? "Venteliste" : "Book time"}
            </Button>

            {!canBeBooked.isEnabled && (
              <>
                <HorizontalSpacer size="x-small" />
                <ErrorMessage error={canBeBooked.code} errorMessageFlavour={ErrorMessageFlavour.GROUP_ACTIVITY} />
              </>
            )}

            {bookingError && (
              <>
                <HorizontalSpacer size="x-small" />
                <ErrorMessage error={bookingError} errorMessageFlavour={ErrorMessageFlavour.GROUP_ACTIVITY} />
              </>
            )}

            <HorizontalSpacer size="medium" />
          </>
        )}

        {!parts.page.headerHeading && groupActivity && (
          <>
            <Heading type="h2">{groupActivity.page.title}</Heading>
            <HorizontalSpacer size="x-small" />
          </>
        )}

        {bookableActivity.end && bookableActivity.start && (
          <>
            <IconAndText
              icon={
                <>
                  <ClockIcon stripIcon={true} />
                  <VerticalSpacer size="x-small" />
                </>
              }
              size="normal"
              text={`${dayjs(bookableActivity.end).diff(dayjs(bookableActivity.start), "m")} min`}
            />
            <HorizontalSpacer size="large" />
          </>
        )}

        <IconAndText
          icon={
            <>
              <CalendarDaysIcon stripIcon={true} />
              <VerticalSpacer size="x-small" />
            </>
          }
          text={capitalizeWord(
            `${dayjs(bookableActivity.start).format("dddd DD.MM.YYYY kl HH:mm")} - ${dayjs(bookableActivity.end).format(
              "HH:mm",
            )}`,
          )}
        />
        <HorizontalSpacer />

        {bookableActivity.center && (
          <>
            <Link href={paths.senter(bookableActivity.center.slug)}>
              <IconAndText
                icon={
                  <>
                    <MapPinIconOutline stripIcon={true} />
                    <VerticalSpacer size="x-small" />
                  </>
                }
                text={bookableActivity.center.page.title}
              />
            </Link>
            <HorizontalSpacer />
          </>
        )}

        {bookableActivity.instructors.length > 0 && (
          <>
            <Link href={paths.instruktor(bookableActivity.instructors[0].slug)}>
              <IconAndText
                icon={
                  <>
                    <UsersIcon stripIcon={true} />
                    <VerticalSpacer size="x-small" />
                  </>
                }
                text={bookableActivity.instructors[0].page.title}
              />
            </Link>
            <HorizontalSpacer />
          </>
        )}

        {bookableActivity.slot.available > 0 && (
          <>
            <Text size="small" value={`${bookableActivity.slot.available} ledige`} />
            <HorizontalSpacer />
          </>
        )}

        {bookableActivity.slot.inWaitingList > 0 && bookableActivity.slot.available === 0 && (
          <>
            <Text size="small" value={`${bookableActivity.slot.inWaitingList} på venteliste`} />
            <HorizontalSpacer />
          </>
        )}

        {bookableActivity.locations?.length > 0 && (
          <>
            <Text size="small" value={bookableActivity?.locations?.join(", ")} />
            <HorizontalSpacer />
          </>
        )}

        {bookableActivity.externalMessage && (
          <>
            <IconAndText
              icon={
                <>
                  <InfoCircleIcon stripIcon={true} />
                  <VerticalSpacer size="x-small" />
                </>
              }
              style="italic"
              text={bookableActivity.externalMessage}
            />
            <HorizontalSpacer />
          </>
        )}
        <HorizontalSpacer size="medium" />

        {(parts.page.description || parts.page.descriptionRichText) && <HandleTextToRichText page={parts.page} />}
      </ContentContainer>

      <ShareButtons
        calendarEvent={calendarEvent}
        shareTitle={bookableActivityData.BookableGroupActivity.name || bookableActivity.name}
      />

      <DynamicZone blocks={parts.blocks} />
      <HorizontalSpacer size="large" />

      {groupActivity && (
        <BookingNextActivitiesForFilters
          currentFilters={[
            {
              type: FilterTypes.GroupActivity,
              value: groupActivity.id,
            },
          ]}
          take={10}
          showWeekDayFilter={false}
          title={`Kommende ${bookableActivityData.BookableGroupActivity.name}`}
        />
      )}
    </>
  );
};
